import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  PurchaseOrder,
  ReceiveGoodsInput,
  ReorderSuggestions,
  Supplier,
  SupplierStatement,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  getPurchaseOrders,
  getReorderSuggestions,
  getSupplierStatement,
  getSuppliers,
  receiveGoods,
  sendPurchaseOrder,
} from './purchasing-api.js';

/**
 * The purchasing screens' data layer.
 *
 * A goods receipt invalidates STOCK as well as purchasing, because it has just written movements
 * into task 27's ledger — a shop that recorded a delivery and then saw the same empty shelf would
 * reasonably conclude the delivery had not been recorded, and record it again.
 */

export const purchasingKeys = {
  all: ['purchasing'] as const,
  suppliers: () => ['purchasing', 'suppliers'] as const,
  statement: (supplierId: string) => ['purchasing', 'statement', supplierId] as const,
  orders: (openOnly: boolean) => ['purchasing', 'orders', openOnly] as const,
  reorder: () => ['purchasing', 'reorder'] as const,
};

export function useSuppliers(options: { enabled?: boolean } = {}): UseQueryResult<Supplier[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: purchasingKeys.suppliers(),
    queryFn: () => getSuppliers(client),
    enabled: options.enabled ?? true,
  });
}

export function usePurchaseOrders(
  openOnly = true,
  options: { enabled?: boolean } = {},
): UseQueryResult<PurchaseOrder[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: purchasingKeys.orders(openOnly),
    queryFn: () => getPurchaseOrders(client, { openOnly }),
    enabled: options.enabled ?? true,
  });
}

export function useSupplierStatement(
  supplierId: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<SupplierStatement> {
  const client = useApiClient();
  return useQuery({
    queryKey: purchasingKeys.statement(supplierId),
    queryFn: () => getSupplierStatement(client, supplierId),
    enabled: (options.enabled ?? true) && supplierId.length > 0,
  });
}

export function useReorderSuggestions(
  options: { enabled?: boolean } = {},
): UseQueryResult<ReorderSuggestions> {
  const client = useApiClient();
  return useQuery({
    queryKey: purchasingKeys.reorder(),
    queryFn: () => getReorderSuggestions(client),
    enabled: options.enabled ?? true,
  });
}

export function useSendPurchaseOrder(): UseMutationResult<PurchaseOrder, unknown, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (purchaseOrderId: string) => sendPurchaseOrder(client, purchaseOrderId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: purchasingKeys.all });
    },
  });
}

export function useReceiveGoods(
  purchaseOrderId: string,
): UseMutationResult<{ order: PurchaseOrder }, unknown, ReceiveGoodsInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ReceiveGoodsInput) => receiveGoods(client, purchaseOrderId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: purchasingKeys.all });
      // The shelf has just moved. Showing the old quantity would invite a second receipt.
      void queryClient.invalidateQueries({ queryKey: ['stock'] });
    },
  });
}
